import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedRoute, Unauthorized, NotFound } from './auth/ProtectedRoute';
import { useTranslation } from 'react-i18next';
import Login from './auth/Login';
import Layout from './components/Layout';
import './i18n';

import Dashboard from './pages/Home/Dashboard';
import StudentList from './pages/Students/StudentList';
import StudentProfile from './pages/Students/StudentProfile';
import StudentRegistration from './pages/Students/StudentRegistration';
import EditStudent from './pages/Students/EditStudent';
import TeachersList from './pages/Teachers/TeachersList';
import TeacherProfile from './pages/Teachers/TeacherProfile';
import EditTeacher from './pages/Teachers/EditTeacher';
import ParentList from './pages/Parents/ParentList';
import ParentProfile from './pages/Parents/ParentProfile';
import AttendanceSystem from './pages/Attendance/AttendanceSystem';
import AttendanceReport from './pages/Attendance/AttendanceReport';
import SchoolProfile from './pages/School/SchoolProfile';
import SchoolsList from './pages/SuperAdmin/SchoolsList';
import SchoolRegistration from './pages/School/SchoolRegistration';
import ClassManagement from './pages/School/ClassManagement';
import SessionManagement from './pages/School/SessionManagement';
import RequestPasswordReset from './auth/RequestPasswordReset';
import ResetPassword from './auth/ResetPassword';
import EditParent from './pages/Parents/EditParent';
import TeacherRegistration from './pages/Teachers/TeacherRegistration';
import ParentMessaging from './pages/Messages/ParentMessaging';
import SchoolProfileForTeacher from './pages/TeacherPortal/SchoolProfileForTeacher';
import EmailSender from './pages/Messages/EmailSender';
import StudentAttendanceSummary from './pages/Students/StudentAttendanceSummary';
import SubjectCategories from './pages/School/SubjectCategories';
import AcademicYearsManagement from './pages/School/AcademicYearsManagement';
import Subjects from './pages/School/Subjects';
import ExamManagement from './pages/Exams/ExamManagement';
import GradingScales from './pages/Exams/GradingScales';
import ExamConfigManager from './pages/Exams/ExamConfigManager';
import MarkEntry from './pages/Exams/MarkEntry';
import RawScoreResults from './pages/Exams/RawScoreResults';
import ExamsMarkSheet from './pages/Exams/ExamsMarkSheet';
import ExamWeightConfig from './pages/Exams/ExamWeightConfig';
import MeritListAnalytics from './pages/Exams/MeritListAnalytics';
import ReportCardGenerator from './pages/Exams/ReportCardGenerator';
import FeeStructures from './pages/Fees/FeeStructures';
import FeeStructureItems from './pages/Fees/FeeStructureItems';
import BillManagement from './pages/Fees/BillManagement';
import Payment from './pages/Fees/Payment';
import FeeManagementDashboard from './pages/Fees/FeeManagementDashboard';
import StudentPaymentHistory from './pages/Fees/StudentPaymentHistory';

const LoadingSpinner = () => {
  return (
    <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );
};


const App = () => {
  const schoolOperationRoles = ["super_admin", "school_admin"];
  const teacherRoles = ["super_admin", "school_admin", "teacher"];
  const superAdminRoles = ["super_admin"];
  const { i18n } = useTranslation();

  React.useEffect(() => {
    document.documentElement.dir = i18n.dir();
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<RequestPasswordReset />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Protected layout wrapper */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          {/* Dashboard accessible to all authenticated users */}
          <Route index element={<Dashboard />} />

          <Route path="/all-teachers" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <TeachersList />
            </ProtectedRoute>
          } />
          <Route path="/register-teacher" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <TeacherRegistration />
            </ProtectedRoute>
          } />
          <Route path="/teacher-profile/:teacherId" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <TeacherProfile />
            </ProtectedRoute>
          } />
          <Route path="/edit-teacher/:teacherId" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <EditTeacher />
            </ProtectedRoute>
          } />
          <Route path="/all-students" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <StudentList />
            </ProtectedRoute>
          } />
          <Route path="/student-profile/:studentId" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <StudentProfile />
            </ProtectedRoute>
          } />
          <Route path="/student-attendance-report/:studentId" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <StudentAttendanceSummary />
            </ProtectedRoute>
          } />
          <Route path="/register-student" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <StudentRegistration />
            </ProtectedRoute>
          } />
          <Route path="/edit-student/:studentId" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <EditStudent />
            </ProtectedRoute>
          } />
          <Route path="/all-parents" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ParentList />
            </ProtectedRoute>
          } />
          <Route path="/parent-profile/:parentId" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ParentProfile />
            </ProtectedRoute>
          } />
          <Route path="/edit-parent/:parentId" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <EditParent />
            </ProtectedRoute> 
          } />

          <Route path="/mark-attendance" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <AttendanceSystem />
            </ProtectedRoute>
          } />
          <Route path="/attendance-report" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <AttendanceReport />
            </ProtectedRoute>
          } />
          <Route path="/exam-management" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ExamManagement />
            </ProtectedRoute>
          } />
          <Route path="/grading-scales" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <GradingScales />
            </ProtectedRoute>
          } />
          <Route path="/exam-config-manager/:examId" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ExamConfigManager />
            </ProtectedRoute>
          } />

          <Route path="/mark-entry" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <MarkEntry />
            </ProtectedRoute>
          } />

          <Route path="/exam-raw-scores" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <RawScoreResults />
            </ProtectedRoute>
          } />
          <Route path="/raw-mark-sheet" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ExamsMarkSheet />
            </ProtectedRoute>
          } />
          <Route path="/exan-merit-list" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <MeritListAnalytics />
            </ProtectedRoute>
          } />

          <Route path="/report-card-generator" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ReportCardGenerator />
            </ProtectedRoute>
          } />

          <Route path="/exams-combination" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <ExamWeightConfig />
            </ProtectedRoute>
          } />

          <Route path="/send-sms" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <ParentMessaging />
            </ProtectedRoute>
          } />

          <Route path="/send-email" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <EmailSender />
            </ProtectedRoute>
          } />

          <Route path="/school-teacher-profile" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <SchoolProfileForTeacher />
            </ProtectedRoute>
          } />

          <Route path="/school-profile" element={
            <ProtectedRoute allowedRoles={teacherRoles}>
              <SchoolProfile />
            </ProtectedRoute>
          } />
          <Route path="/register-school" element={
            <ProtectedRoute allowedRoles={superAdminRoles}>
              <SchoolRegistration />
            </ProtectedRoute>
          } />
          <Route path="/all-schools" element={
            <ProtectedRoute allowedRoles={superAdminRoles}>
              <SchoolsList />
            </ProtectedRoute>
          } />

          <Route path="/class-management" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <ClassManagement />
            </ProtectedRoute>
          } />
          <Route path="/session-management" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <SessionManagement />
            </ProtectedRoute>
          } />
          <Route path="/subject-categories" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <SubjectCategories />
            </ProtectedRoute>
          } />
          <Route path="/subjects" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <Subjects />
            </ProtectedRoute>
          } />
          <Route path="/academic-year-management" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <AcademicYearsManagement /> 
            </ProtectedRoute>
          } />
          <Route path="/fees-dashboard" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <FeeManagementDashboard />
            </ProtectedRoute>
          } />
          <Route path="/fee-management/:classId/student/:studentId/payments" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <StudentPaymentHistory />
            </ProtectedRoute>
          } />
          <Route path="/fees-structure" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <FeeStructures />
            </ProtectedRoute>
          } />
          <Route path="/fees-structure/:id" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <FeeStructureItems />
            </ProtectedRoute>
          } />
          <Route path="/billmanagement" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <BillManagement /> 
            </ProtectedRoute>
          } />
          <Route path="/payment" element={
            <ProtectedRoute allowedRoles={schoolOperationRoles}>
              <Payment /> 
            </ProtectedRoute>
          } />
        </Route>

        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default App;